import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Quote, Plus, X } from "lucide-react";
import { toast } from "sonner";

export const SayingsEditor = () => {
  const [open, setOpen] = useState(false);
  const [sayings, setSayings] = useState([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (open) api.get("/admin/sayings").then((r) => setSayings(r.data.sayings)).catch(() => {});
  }, [open]);

  const save = async (next) => {
    try {
      const r = await api.put("/admin/sayings", { sayings: next });
      setSayings(r.data.sayings);
      return true;
    } catch {
      toast.error("Couldn't save your sayings");
      return false;
    }
  };

  const add = async () => {
    if (!draft.trim()) return;
    if (await save([...sayings, draft])) {
      setDraft("");
      toast.success("Saying added. India will start using it");
    }
  };

  const remove = (i) => save(sayings.filter((_, idx) => idx !== i));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="open-sayings-editor"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#2A3B32]/15 text-sm text-[#1C221D] hover:bg-[#2A3B32] hover:text-[#FDFBF7] transition-colors duration-200"
        >
          <Quote size={14} /> Signature sayings
        </button>
      </DialogTrigger>
      <DialogContent data-testid="sayings-dialog" className="bg-white rounded-3xl border-[#2A3B32]/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Your signature sayings</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#5C6A61]">
          India will weave these into replies when they fit the moment, no more than one per reply.
        </p>
        <ul className="mt-2 space-y-2 max-h-72 overflow-y-auto chat-scroll" data-testid="sayings-list">
          {sayings.length === 0 && <li className="text-sm text-[#5C6A61] italic">No sayings yet. Add your first one below.</li>}
          {sayings.map((s, i) => (
            <li key={`${s}-${i}`} className="flex items-start gap-3 bg-[#F2EBE5] rounded-xl px-4 py-3" data-testid={`saying-${i}`}>
              <span className="flex-1 font-serif-display italic text-lg text-[#1C221D] leading-snug">"{s}"</span>
              <button onClick={() => remove(i)} data-testid={`remove-saying-${i}`} className="text-[#5C6A61] hover:text-[#E07A5F] mt-1">
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2 mt-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="e.g. Baby, you already have the answer."
            data-testid="saying-input"
            className="flex-1 px-4 py-3 rounded-xl border border-[#2A3B32]/15 bg-[#FDFBF7] focus:outline-none focus:border-[#2A3B32]"
          />
          <button
            onClick={add}
            disabled={!draft.trim()}
            data-testid="add-saying-btn"
            className="px-4 rounded-xl bg-[#2A3B32] text-[#FDFBF7] disabled:opacity-50 active:scale-95 transition-transform"
          >
            <Plus size={18} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SayingsEditor;
